// =============================================================================
// ui/share/SharedTradeModal.tsx — the read-only trade a VISITOR opens.
// -----------------------------------------------------------------------------
// ── 🚨 IT IS NOT THE REAL TRADE WINDOW, AND COULD NOT BE ──
//
// `TradeDetail` builds live inputs whose option lists come from the signed-in
// user's registries, wires a save path, and mounts a chart fed by an
// authenticated, metered market-data endpoint. A visitor has no session and no
// registries: most of it throws, and the chart would be a paid data leak.
//
// This renders the same annotations from one plain object in the payload.
//
// ── READ-ONLY, CONCRETELY ──
//
// No Delete, no Save, no Coach Me, no live chart, and not a single `<input>`,
// `<select>` or `<textarea>` anywhere in it. Every flag is a static tick or
// cross.
// =============================================================================

import { useState } from 'react';
import { money, num } from '../format';
import type { ShareTrade } from '../../selectors/share';

const TABS = [
  ['ov', 'Overview'], ['ex', 'Execution'], ['rk', 'Risk'],
  ['su', 'Setup'], ['pr', 'Process'], ['nm', 'Notes & Media'],
] as const;

type TabId = (typeof TABS)[number][0];

export interface SharedTradeModalProps {
  trade: ShareTrade;
  /** The day's trades, in the order the table lists them — for ‹ ›. */
  all: readonly ShareTrade[];
  index: number;
  onNavigate: (index: number) => void;
  onClose: () => void;
}

export function SharedTradeModal(
  { trade: td, all, index, onNavigate, onClose }: SharedTradeModalProps,
) {
  const [tab, setTab] = useState<TabId>('ov');
  const shots = td.shots ?? [];
  const breaches = (td.discipline ?? []).filter((d) => d.tripped);
  const many = all.length > 1;

  return (
    <div className="modal-scrim sr-modal-scrim" role="dialog" aria-modal="true"
      aria-label={`${td.contract || 'Trade'} ${td.date}`}>
      <div className="modal wide">
        <header className="modal-head">
          <b>{`${td.contract || 'Trade'} · ${td.date}${td.timeIn ? ` ${td.timeIn}` : ''}`}</b>
          <button type="button" className="btn" onClick={onClose}>×</button>
        </header>

        <div className="modal-body sr-tw">
          {/* ── The media area, full width on top ──────────────────────────
              ⚠ The owner's window puts a LIVE CHART here, fed by a metered
              endpoint. A visitor has no session, so this pane is the sharer's
              own screenshots or nothing — and it SAYS which, rather than
              rendering an empty frame that reads as broken. */}
          {shots.length ? (
            <div className="sr-shots">
              {shots.map((u, i) => (
                <img key={i} src={u} alt={`Chart ${i + 1}`} loading="lazy" />
              ))}
            </div>
          ) : (
            <div className="sr-shots-empty muted">
              No chart screenshots were included in this share.
            </div>
          )}

          <div className="sr-tabs" role="tablist">
            {TABS.map(([id, label]) => (
              <button
                key={id} type="button" role="tab"
                aria-selected={tab === id}
                className={`sr-tab${tab === id ? ' active' : ''}`}
                onClick={() => setTab(id)}
              >{label}</button>
            ))}
          </div>

          {tab === 'ov' ? (
            <Stats rows={[
              ['Net P&L', money(td.netPnl), td.netPnl != null && td.netPnl < 0 ? 'red' : 'green'],
              ['Side', (td.side || '').toUpperCase(), td.side === 'long' ? 'green' : 'red'],
              ['Contracts', td.qty ?? '—'],
              ['Points', num(td.points)],
              ['Realized R', td.rrRealized == null ? '—' : `${num(td.rrRealized)}R`],
              ['Grade', td.grade || '—'],
              ['Account', td.account || '—'],
            ]} />
          ) : null}

          {tab === 'ex' ? (
            <>
              <Stats rows={[
                ['Date', td.date],
                ['Time in', td.timeIn || '—'],
                ['Time out', td.timeOut || '—'],
                ['Entry', num(td.entry)],
                ['Exit', num(td.exit)],
                ['Quantity', td.qty ?? '—'],
              ]} />
              {/* ⚠ SAYS WHAT IS MISSING, rather than leaving a reviewer to
                  wonder. The fill tape is one of the four things that never
                  travel — it is the sizing method rather than the result. */}
              <p className="muted sr-note">
                The fill-by-fill breakdown is not included in shared reports —
                entry and exit are the volume-weighted averages.
              </p>
            </>
          ) : null}

          {tab === 'rk' ? (
            <>
              <Stats rows={[
                ['Planned R', td.rrPlanned == null ? '—' : `${num(td.rrPlanned)}R`],
                ['Realized R', td.rrRealized == null ? '—' : `${num(td.rrRealized)}R`],
                ['Risk (points)', num(td.riskPoints)],
                ['Planned stop', num(td.plannedStop)],
                ['Planned target', num(td.plannedTarget)],
                ['MAE (points)', num(td.maePoints), 'red'],
                ['MFE (points)', num(td.mfePoints), 'green'],
              ]} />
              <p className="muted sr-note">
                Risk is shown in points and R. The dollar figure is not included
                in shared reports.
              </p>
            </>
          ) : null}

          {tab === 'su' ? (
            <Stats rows={[
              ['Pattern', td.pattern || '—'],
              ['Exit model', td.exitModel || '—'],
              ['Grade', td.grade || '—'],
              ['Session', td.session || '—'],
              ['Market regime', td.regime || '—'],
            ]} />
          ) : null}

          {tab === 'pr' ? (
            <>
              {td.reviewed ? (
                <>
                  <div className="sr-flags">
                    {(td.discipline ?? []).map((d) => (
                      <span key={d.label} className={d.tripped ? 'red' : 'muted'}>
                        {d.tripped ? '✕' : '✓'} {d.label}
                      </span>
                    ))}
                  </div>
                  <p className="muted sr-note">
                    {breaches.length
                      ? `${breaches.length} breach${breaches.length === 1 ? '' : 'es'} flagged on this trade.`
                      : 'No discipline breaches flagged.'}
                  </p>
                </>
              ) : (
                /* ⚠ AN UNREVIEWED TRADE MUST NOT RENDER EIGHT UNTICKED BOXES.
                   The flags are stored as "absent means it did not happen", so
                   that would show a clean sheet for a trade nobody has opened —
                   a claim about the trader, made on their behalf. */
                <p className="muted sr-note">
                  This trade was never reviewed, so no discipline assessment was
                  recorded. The boxes are not shown, because an unreviewed trade
                  would otherwise read as a clean sheet.
                </p>
              )}
              {(td.mistakeTags.length || td.tags.length) ? (
                <div className="sr-flags sr-flags-tags">
                  {td.mistakeTags.map((x) => (
                    <span key={x} className="tag red">{x}</span>
                  ))}
                  {td.tags.map((t) => (
                    <span
                      key={t.name}
                      className="tag"
                      /* ⚠ Validated to a `#rrggbb` literal at capture time —
                         see domain/share.ts. An empty colour styles nothing. */
                      {...(t.color ? { style: { borderColor: t.color, color: t.color } } : {})}
                    >{t.name}</span>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}

          {tab === 'nm' ? (
            (td.notes || td.newsEvent || td.mood) ? (
              <>
                {td.newsEvent ? <p className="muted sr-note"><b>News:</b> {td.newsEvent}</p> : null}
                {td.mood ? <p className="muted sr-note"><b>Mood:</b> {td.mood}</p> : null}
                {td.notes ? <p className="sr-notes">{td.notes}</p> : null}
              </>
            ) : (
              <p className="muted sr-note">
                The sharer did not include written notes in this link.
              </p>
            )
          ) : null}

          <p className="muted sr-privacy">
            Read-only shared view. Voice notes, the fill-by-fill breakdown and
            dollar risk are never included in a shared report.
          </p>
        </div>

        <footer className="modal-foot">
          {/* ⚠ Prev/next across the report's trades, because a reviewer works
              through them in order — clicking Close and finding the next row is
              the friction this whole feature exists to remove. */}
          <button
            type="button" className="btn" disabled={!many}
            onClick={() => onNavigate((index - 1 + all.length) % all.length)}
          >‹ Prev</button>
          <button
            type="button" className="btn" disabled={!many}
            onClick={() => onNavigate((index + 1) % all.length)}
          >Next ›</button>
          <span className="grow" />
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </footer>
      </div>
    </div>
  );
}

function Stats({ rows }: { rows: readonly (readonly [string, string | number, string?])[] }) {
  return (
    <div className="sr-stats">
      {rows.map(([k, v, cls]) => (
        <div key={k} className="sr-row">
          <span className="sr-k">{k}</span>
          <span className={`sr-v ${cls ?? ''}`}>{v}</span>
        </div>
      ))}
    </div>
  );
}
